/**
 * Wearables — Heart-rate live (Web Bluetooth) + Activity file upload (.GPX / .TCX).
 *
 * Notes:
 * - Web Bluetooth is supported in Chrome/Edge/Android. iOS Safari is unsupported,
 *   so we feature-detect and show an upload-only fallback (Apple Watch/Suunto users
 *   can export their activity as .GPX from the Health app or vendor app).
 * - The standard BLE Heart Rate Service (UUID 0x180D) + Heart Rate Measurement
 *   characteristic (0x2A37) is used. Parses the 8-bit/16-bit format flag.
 */
import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Heartbeat, Watch, UploadSimple, Pulse, Trash, Lightning, Path, Timer, Warning } from "@phosphor-icons/react";

const HR_SERVICE = 0x180d;
const HR_CHAR = 0x2a37;

function parseHrMeasurement(value) {
  // value is a DataView. First byte = flags. If bit 0 = 0, HR is uint8; else uint16.
  const flag = value.getUint8(0);
  if (flag & 0x01) return value.getUint16(1, true);
  return value.getUint8(1);
}

function fmtDuration(s) {
  if (!s || s < 0) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function fmtDistance(m) {
  if (!m) return "—";
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

export default function Wearables() {
  const [sessions, setSessions] = useState([]);
  const [supported, setSupported] = useState(true);
  const [device, setDevice] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [currentHr, setCurrentHr] = useState(null);
  const [samples, setSamples] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const charRef = useRef(null);

  useEffect(() => {
    setSupported(typeof navigator !== "undefined" && !!navigator.bluetooth);
    load();
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/wearables/sessions");
      setSessions(data || []);
    } catch { /* silent */ }
  };

  const connect = async () => {
    if (!navigator.bluetooth) {
      toast.error("Tu navegador no soporta Web Bluetooth. Sube un archivo .GPX/.TCX.");
      return;
    }
    try {
      const dev = await navigator.bluetooth.requestDevice({
        filters: [{ services: [HR_SERVICE] }],
        optionalServices: [HR_SERVICE],
      });
      const server = await dev.gatt.connect();
      const service = await server.getPrimaryService(HR_SERVICE);
      const ch = await service.getCharacteristic(HR_CHAR);
      await ch.startNotifications();
      ch.addEventListener("characteristicvaluechanged", (e) => {
        const hr = parseHrMeasurement(e.target.value);
        setCurrentHr(hr);
        setSamples((prev) => [...prev, { t: Date.now(), hr }]);
      });
      charRef.current = ch;
      setDevice(dev);
      setTracking(true);
      setStartedAt(new Date().toISOString());
      setSamples([]);
      toast.success(`Conectado: ${dev.name || "Dispositivo"}`);
      dev.addEventListener("gattserverdisconnected", () => {
        setTracking(false);
        toast.warning("Dispositivo desconectado");
      });
    } catch (err) {
      toast.error("No se pudo conectar: " + (err.message || "cancelado"));
    }
  };

  const stopAndSave = async () => {
    if (!samples.length) {
      toast.error("Sin muestras para guardar");
      return;
    }
    try {
      if (charRef.current) {
        await charRef.current.stopNotifications().catch(() => null);
      }
      if (device?.gatt?.connected) device.gatt.disconnect();
    } catch { /* ignore */ }
    const hrs = samples.map((s) => s.hr).filter(Boolean);
    const ended = new Date().toISOString();
    const duration_s = startedAt ? Math.round((Date.parse(ended) - Date.parse(startedAt)) / 1000) : 0;
    try {
      await api.post("/wearables/sessions", {
        device_name: device?.name || "BLE Heart Rate",
        started_at: startedAt,
        ended_at: ended,
        duration_s,
        avg_hr: Math.round(hrs.reduce((a, b) => a + b, 0) / hrs.length),
        max_hr: Math.max(...hrs),
        min_hr: Math.min(...hrs),
        samples,
      });
      toast.success("Sesión guardada");
      setTracking(false);
      setSamples([]);
      setCurrentHr(null);
      setDevice(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Archivo muy grande (máx 5MB)");
      return;
    }
    setUploading(true);
    try {
      const text = await file.text();
      await api.post("/wearables/activity-upload", { filename: file.name, content: text });
      toast.success("Actividad importada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta sesión?")) return;
    try {
      await api.delete(`/wearables/sessions/${id}`);
      toast.success("Eliminada");
      load();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wearables-page">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Wearables</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Datos de tu juego</h1>
        <p className="text-sm text-gray-400 mt-1 max-w-xl">Conecta tu reloj/banda BLE en vivo o sube actividades exportadas desde Apple Watch, Suunto, Garmin (formato .GPX o .TCX).</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Live BLE */}
        <div className="card-tactical p-5" data-testid="wearables-live-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 flex items-center justify-center border border-red-500/40 bg-red-500/10 text-red-400">
              <Heartbeat size={24} weight="duotone" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-red-400 font-semibold">En vivo</p>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Frecuencia Cardíaca BLE</h2>
            </div>
          </div>

          {!supported ? (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] p-3" data-testid="wearables-unsupported">
              <Warning size={14} className="inline mr-1" /> Web Bluetooth no disponible en este navegador (no funciona en iOS Safari). Usa Chrome/Edge/Android, o sube un archivo .GPX/.TCX.
            </div>
          ) : !tracking ? (
            <Button onClick={connect} className="w-full bg-red-500 text-white hover:bg-red-600 font-bold uppercase tracking-wide text-xs h-10" data-testid="wearables-connect-btn">
              <Watch size={16} className="mr-2" /> Conectar reloj/banda
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="bg-[#0A0A0A] border border-red-500/30 p-4 text-center">
                <p className="font-['Barlow_Condensed'] font-black text-6xl text-red-400 leading-none" data-testid="wearables-current-hr">
                  {currentHr || "—"}
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">BPM · {samples.length} muestras</p>
                {device?.name && <p className="text-[10px] text-gray-400 mt-2 truncate">📡 {device.name}</p>}
              </div>
              <Button onClick={stopAndSave} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs h-10" data-testid="wearables-stop-btn">
                Detener y guardar
              </Button>
            </div>
          )}
          <p className="text-[10px] text-gray-500 mt-3">Compatible con cualquier banda/reloj con BLE Heart Rate Service estándar (Polar, Garmin, Suunto, Wahoo, etc.).</p>
        </div>

        {/* GPX/TCX Upload */}
        <div className="card-tactical p-5" data-testid="wearables-upload-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 flex items-center justify-center border border-blue-500/40 bg-blue-500/10 text-blue-400">
              <UploadSimple size={24} weight="duotone" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-400 font-semibold">Importar</p>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Apple Watch / Suunto / Garmin</h2>
            </div>
          </div>

          <label className="block">
            <input
              type="file"
              accept=".gpx,.tcx,application/gpx+xml,application/xml,text/xml"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
              data-testid="wearables-upload-input"
            />
            <div className="border-2 border-dashed border-blue-500/30 hover:border-blue-500/60 transition-colors p-6 text-center cursor-pointer">
              {uploading ? (
                <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent animate-spin mx-auto" />
              ) : (
                <>
                  <UploadSimple size={32} className="text-blue-400 mx-auto mb-2" weight="duotone" />
                  <p className="text-sm text-white font-semibold">Click para subir</p>
                  <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-widest">.GPX · .TCX · máx 5MB</p>
                </>
              )}
            </div>
          </label>
          <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
            <strong className="text-gray-300">Apple Watch:</strong> Exporta desde la app Salud (Health) → Compartir → GPX.<br/>
            <strong className="text-gray-300">Suunto:</strong> Exporta desde Suunto App → Mover → Exportar como .gpx.<br/>
            <strong className="text-gray-300">Garmin:</strong> Garmin Connect → Actividad → ⋯ → Exportar Original.
          </p>
        </div>
      </div>

      {/* History */}
      <div>
        <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase mb-3">Historial de sesiones</h2>
        {sessions.length === 0 ? (
          <div className="card-tactical p-8 text-center">
            <Pulse size={32} className="text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sin sesiones aún. Conecta tu wearable o sube una actividad.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="wearables-sessions-list">
            {sessions.map((s) => (
              <div key={s.id} className="card-tactical p-4 flex items-center gap-4 flex-wrap" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={s.source === "web_bluetooth" ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}>
                    {s.source === "web_bluetooth" ? <Heartbeat size={12} className="mr-1" /> : <Path size={12} className="mr-1" />}
                    {s.source === "web_bluetooth" ? "BLE LIVE" : (s.format || "GPX").toUpperCase()}
                  </Badge>
                  <p className="text-[10px] text-gray-500 font-['JetBrains_Mono']">{(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap text-xs">
                  {s.avg_hr ? (
                    <span className="flex items-center gap-1 text-red-400" data-testid={`session-${s.id}-hr`}>
                      <Heartbeat size={12} /> <strong>{s.avg_hr}</strong>/<strong>{s.max_hr}</strong> bpm
                    </span>
                  ) : null}
                  {s.duration_s ? <span className="flex items-center gap-1 text-gray-300"><Timer size={12} /> {fmtDuration(s.duration_s)}</span> : null}
                  {s.distance_m ? <span className="flex items-center gap-1 text-gray-300"><Path size={12} /> {fmtDistance(s.distance_m)}</span> : null}
                  {s.device_name ? <span className="text-gray-500 truncate max-w-[140px]">📡 {s.device_name}</span> : null}
                  {s.filename ? <span className="text-gray-500 truncate max-w-[180px]">📄 {s.filename}</span> : null}
                </div>
                <div className="ml-auto">
                  <Button onClick={() => remove(s.id)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`session-delete-${s.id}`}>
                    <Trash size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
